import { TIER_COLORS, PARAMETER_NAMES, TIER_PARAMETERS, TIER_INFO, getGrade, formatNumber, Badge, TierGauge, CTATeaser, badgeColors } from "./DashboardShared";

// =====================================================
// TAB: DIGITAL DIAGNOSTIC
// =====================================================
const DiagnosticTab = ({ data, tierScores, onTabChange }) => {
  const financialMetrics = data?.financialMetrics || data?.metrics || {};
  const paritySnapshot = data?.paritySnapshot || data?.parity_snapshot || {};
  const googleListingSnapshot = data?.googleListingSnapshot || data?.google_listing_snapshot || {};
  const serpSnapshot = data?.serpSnapshot || data?.serp_snapshot || {};
  const pagespeedSnapshot = data?.pagespeedSnapshot || data?.pagespeed_snapshot || {};
  const monthlyVisitors = financialMetrics.monthlyVisitors || 1240;
  const benchmarkVisitors = financialMetrics.benchmarkVisitors || 4800;
  const currentCvr = parseFloat(financialMetrics.currentCvrPercent || "0.56%");
  const potentialCvr = 1.5;
  
  const tier1Params = ["brandSearchVisibility", "rateParityChannelCompetitiveness", "gmbOptimisation"];
  const tier2Params = ["pageLoadPerformance", "mobileBookingExperience", "bookingSimplicity"];
  const tier3Params = ["directBookingIncentive", "contentDepthQuality", "socialProofReviewSignals", "paymentTrustSecurity"];

  const getFindingsForTier = (tierParams) => {
    const actionPlan = data?.actionPlan || data?.action_plan || [];
    const scores = data?.scores || {};

    const sorted = tierParams
      .map(p => ({ param: p, score: scores[p] || 10 }))
      .sort((a, b) => a.score - b.score);

    const result = [];

    // First pass: one finding from each param with issues (score < 8)
    sorted.forEach(({ param }) => {
      const item = actionPlan.find(a => a.parameter === param);
      if (item?.findings?.length > 0) {
        const score = scores[param] || 10;
        if (score < 8) result.push(item.findings[0]);
      }
    });

    // Second pass: fill remaining slots from worst params
    sorted.forEach(({ param }) => {
      if (result.length >= 4) return;
      const item = actionPlan.find(a => a.parameter === param);
      if (item?.findings?.length > 1) {
        item.findings.slice(1).forEach(f => {
          if (result.length < 4 && !result.includes(f)) result.push(f);
        });
      }
    });

    return result.slice(0, 4);
  };

  const tiers = [
    {
      tier: 1,
      chartLabel: "Monthly Organic Visits",
      current: monthlyVisitors,
      potential: benchmarkVisitors,
      pctReached: benchmarkVisitors > 0 ? Math.round((monthlyVisitors / benchmarkVisitors) * 100) : 0,
      uplift: benchmarkVisitors > monthlyVisitors ? `+${formatNumber(benchmarkVisitors - monthlyVisitors)} visits available` : "+0 visits",
      findings: getFindingsForTier(tier1Params)
    },
    {
      tier: 2,
      chartLabel: "Booking Experience Score",
      current: tierScores[2] || 38,
      potential: 75,
      pctReached: Math.round(((tierScores[2] || 38) / 75) * 100),
      uplift: `+${75 - (tierScores[2] || 38)} pts available`,
      findings: getFindingsForTier(tier2Params)
    },
    {
      tier: 3,
      chartLabel: "Estimated Conversion Rate",
      current: currentCvr,
      potential: potentialCvr,
      pctReached: currentCvr > 0 ? Math.round((currentCvr / potentialCvr) * 100) : 0,
      uplift: currentCvr > 0 ? `+${Math.round(((potentialCvr - currentCvr) / currentCvr) * 100)}% conversion available` : "+0%",
      isPercent: true,
      findings: getFindingsForTier(tier3Params)
    }
  ];
  
  return (
    <div>
      <div className="print-section-title">Digital Diagnostic</div>
      {tiers.map(t => {
        const tierColor = TIER_COLORS[t.tier];
        const tierInfo = TIER_INFO[t.tier];
        const tierScore = tierScores[t.tier] || 0;
        const tierGrade = getGrade(tierScore, 100);
        
        return (
          <div key={t.tier} style={{ background: "#FFFFFF", border: "1px solid rgba(10,10,26,0.08)", borderRadius: 14, marginBottom: 14, overflow: "hidden", position: "relative" }}>
            <div style={{ position: "absolute", top: 0, left: 0, bottom: 0, width: 3, background: tierColor }} />
            <div style={{ padding: "22px 24px 22px 28px", display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 15, fontWeight: 700, color: "#0A0A1A" }}>{tierInfo.name}</span>
                  <Badge grade={tierGrade} />
                </div>
                <div style={{ fontSize: 11.5, color: "rgba(10,10,26,0.4)" }}>{tierInfo.question}</div>
              </div>
              <div style={{ padding: 16, background: `rgba(${t.tier === 1 ? "45,77,224" : t.tier === 2 ? "26,58,184" : "15,42,138"},0.04)`, borderRadius: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 10 }}>
                  <div>
                    <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 30, fontWeight: 800, color: tierColor, lineHeight: 1, letterSpacing: -1 }}>
                      {t.isPercent ? `${typeof t.current === 'number' ? t.current.toFixed(2) : t.current}%` : formatNumber(t.current)}
                    </div>
                    <div style={{ fontSize: 10, color: "rgba(10,10,26,0.4)", marginTop: 3 }}>{t.chartLabel}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 17, fontWeight: 700, color: "rgba(10,10,26,0.25)", lineHeight: 1 }}>
                      {t.isPercent ? `${t.potential.toFixed(2)}%` : formatNumber(t.potential)}
                    </div>
                    <div style={{ fontSize: 10, color: "rgba(10,10,26,0.3)", marginTop: 3 }}>Your Potential</div>
                  </div>
                </div>
                <div style={{ height: 6, background: "rgba(10,10,26,0.08)", borderRadius: 3, overflow: "hidden", marginBottom: 8 }}>
                  <div style={{ height: "100%", width: `${t.pctReached}%`, background: tierColor, borderRadius: 3 }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 10, color: "rgba(10,10,26,0.35)" }}>{t.pctReached}% of potential reached</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: tierColor }}>{t.uplift}</span>
                </div>
              </div>
              <div style={{ height: 1, background: "rgba(10,10,26,0.05)" }} />
              <div>
                <div style={{ fontSize: 9.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: "rgba(10,10,26,0.3)", marginBottom: 10 }}>Top Issues</div>
                {t.findings.slice(0, 4).map((finding, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 0", borderBottom: i < 3 ? "1px solid rgba(10,10,26,0.04)" : "none" }}>
                    <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#DC2626", flexShrink: 0, marginTop: 5 }} />
                    <span style={{ fontSize: 12, color: "rgba(10,10,26,0.6)", lineHeight: 1.5 }}>{finding}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })}
      
      <CTATeaser onTabChange={onTabChange} />
    </div>
  );
};

export default DiagnosticTab;
